// Iter 82.1 / Phase E1.1 — Global Leave Ledger Explorer.
// Audit/reconciliation surface for HR + Accounts + Management. All
// filters compose: employee, department, leave type, fiscal year,
// movement type, actor, period, and date range. Streaming CSV export
// honours the currently-applied filters.
import { useCallback, useEffect, useState } from "react";
import { FunnelSimple, DownloadSimple, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const MOVEMENT_TYPES = [
    "opening_balance", "accrual", "manual_adjustment",
    "leave_taken", "leave_cancelled", "carry_forward",
    "encashment", "expiry", "lop_charge",
];

const MOVEMENT_TONE = {
    opening_balance: "bg-slate-100 text-slate-700",
    accrual: "bg-emerald-100 text-emerald-700",
    manual_adjustment: "bg-amber-100 text-amber-700",
    leave_taken: "bg-rose-100 text-rose-700",
    leave_cancelled: "bg-sky-100 text-sky-700",
    carry_forward: "bg-indigo-100 text-indigo-700",
    encashment: "bg-violet-100 text-violet-700",
    expiry: "bg-orange-100 text-orange-700",
    lop_charge: "bg-red-100 text-red-700",
};

const EMPTY_FILTERS = {
    employee_id: "",
    department: "",
    leave_type_code: "",
    fiscal_year: "",
    movement_type: "",
    period: "",
    from_date: "",
    to_date: "",
};

export default function LeaveLedgerExplorer() {
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0, page_size: 50 });
    const [loading, setLoading] = useState(true);
    const [fys, setFys] = useState([]);
    const [hrMeta, setHrMeta] = useState({ departments: [] });

    const loadMeta = useCallback(async () => {
        try {
            const [fyResp, hmResp] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/hr/employee/meta"),
            ]);
            setFys(fyResp.data.items || []);
            setHrMeta(hmResp.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load metadata"));
        }
    }, []);

    const reload = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = { page, page_size: 50, sort: "created_desc" };
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: payload } = await api.get("/leave/ledger/list", { params });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load ledger"));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(1); }, [reload]);

    const exportCsv = async () => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const { data: blob } = await api.get("/leave/ledger/export", { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `leave-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "CSV export failed"));
        }
    };

    const resetFilters = () => setFilters(EMPTY_FILTERS);
    const activeFilters = Object.entries(filters).filter(([, v]) => !!v);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="leave-ledger-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Leave Ledger Explorer"
                description="Append-only audit log of every leave balance movement. Filter, drill, and export for reconciliation."
                actions={(
                    <Button variant="outline" size="sm" onClick={exportCsv} data-testid="lle-export-btn">
                        <DownloadSimple size={14} className="mr-1" /> Export CSV
                    </Button>
                )}
            />

            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4" data-testid="lle-filters">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <FunnelSimple size={14} /> Filters
                    </div>
                    <Button variant="ghost" size="sm" onClick={resetFilters} disabled={activeFilters.length === 0} data-testid="lle-clear-btn">
                        <X size={12} className="mr-1" /> Clear Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ""}
                    </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                        <label className="text-xs text-slate-600">Employee ID / Code</label>
                        <Input
                            data-testid="lle-emp-id"
                            placeholder="EMP-0001 or EMP-xxx"
                            value={filters.employee_id}
                            onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Department</label>
                        <select
                            data-testid="lle-dept"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.department}
                            onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                        >
                            <option value="">All departments</option>
                            {(hrMeta.departments || []).map((d) => (
                                <option key={d.id || d.name} value={d.name}>{d.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Leave Type</label>
                        <Input
                            data-testid="lle-type"
                            placeholder="CL / SL / EL"
                            value={filters.leave_type_code}
                            onChange={(e) => setFilters({ ...filters, leave_type_code: e.target.value.toUpperCase() })}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Fiscal Year</label>
                        <select
                            data-testid="lle-fy"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.fiscal_year}
                            onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}
                        >
                            <option value="">All FYs</option>
                            {fys.map((fy) => (<option key={fy.id} value={fy.code}>{fy.code}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Movement Type</label>
                        <select
                            data-testid="lle-movement"
                            className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={filters.movement_type}
                            onChange={(e) => setFilters({ ...filters, movement_type: e.target.value })}
                        >
                            <option value="">All movements</option>
                            {MOVEMENT_TYPES.map((m) => (<option key={m} value={m}>{m.replace(/_/g, " ")}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">Period (YYYY-MM)</label>
                        <Input
                            data-testid="lle-period"
                            placeholder="2026-04"
                            value={filters.period}
                            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">From Date</label>
                        <Input
                            type="date"
                            data-testid="lle-from"
                            value={filters.from_date}
                            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-600">To Date</label>
                        <Input
                            type="date"
                            data-testid="lle-to"
                            value={filters.to_date}
                            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                            className="mt-1"
                        />
                    </div>
                </div>
            </section>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="lle-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-left px-3 py-2 font-medium">Leave Type</th>
                                <th className="text-left px-3 py-2 font-medium">Movement</th>
                                <th className="text-right px-3 py-2 font-medium">Delta</th>
                                <th className="text-right px-3 py-2 font-medium">Balance After</th>
                                <th className="text-left px-3 py-2 font-medium">FY · Period</th>
                                <th className="text-left px-3 py-2 font-medium">Reference</th>
                                <th className="text-left px-3 py-2 font-medium">Created By</th>
                                <th className="text-left px-3 py-2 font-medium">Created At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={10} className="text-center py-10 text-slate-400">Loading…</td></tr>
                            )}
                            {!loading && data.items.length === 0 && (
                                <tr><td colSpan={10} className="text-center py-10 text-slate-400">No ledger entries match the current filters.</td></tr>
                            )}
                            {!loading && data.items.map((r) => {
                                const emp = r.employee || {};
                                return (
                                    <tr key={r.id} className="border-t border-slate-100" data-testid={`lle-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-900">{emp.full_name || r.employee_id}</div>
                                            <div className="text-xs text-slate-500 font-mono">{emp.employee_code || r.employee_id}</div>
                                        </td>
                                        <td className="px-3 py-2 text-slate-600 text-xs">{emp.department || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                        <td className="px-3 py-2">
                                            <Badge className={`text-xs ${MOVEMENT_TONE[r.movement_type] || "bg-slate-100"}`}>
                                                {r.movement_type}
                                            </Badge>
                                        </td>
                                        <td className={`px-3 py-2 text-right font-mono ${r.delta_days < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                            {r.delta_days > 0 ? "+" : ""}{Number(r.delta_days).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-right font-semibold">{Number(r.balance_after).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">
                                            <div>{r.fiscal_year}</div>
                                            <div className="text-slate-400">{r.period}</div>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">{r.reference_id || "—"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{r.actor_email || "system"}</td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(r.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {data.pages > 1 && (
                <div className="mt-3 flex items-center justify-between text-xs text-slate-500" data-testid="lle-pager">
                    <div>Page {data.page} of {data.pages} · {data.total} entries</div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => reload(data.page - 1)} data-testid="lle-prev">Prev</Button>
                        <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => reload(data.page + 1)} data-testid="lle-next">Next</Button>
                    </div>
                </div>
            )}
        </div>
    );
}
